import React, { useState } from 'react';
import { X } from 'lucide-react';
import apiService from '../services/api';
import { CompanyAffiliateResource, RegisterReferrerPayload } from '../types';

interface RegisterReferrerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegistered: (affiliate: CompanyAffiliateResource) => void;
}

const emptyForm: RegisterReferrerPayload = {
  name: '',
  whatsapp: '',
  email: '',
  businessName: '',
  businessType: 'fnb',
  otherBusinessType: '',
  city: '',
  termsVersion: '',
  termsAcceptedAt: '',
  privacyAcceptedAt: '',
  registeredVia: 'google_form',
};

/**
 * Form "Daftarkan Referrer".
 *
 * Tidak ada pilihan "user existing / belum" di sini — sengaja. Server yang
 * menentukan percabangannya dari email: kalau akunnya sudah ada, program tinggal
 * diaktifkan; kalau belum, akun dibuat dan tautan set-password dikirim. Admin
 * bisa salah menebak, dan menebak salah berarti akun kedua untuk orang yang sama.
 */
const RegisterReferrerModal: React.FC<RegisterReferrerModalProps> = ({ isOpen, onClose, onRegistered }) => {
  const [form, setForm] = useState<RegisterReferrerPayload>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof RegisterReferrerPayload) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const result = await apiService.registerReferrer(form);
      setForm(emptyForm);
      onRegistered(result.data);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Gagal mendaftarkan referrer.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white mb-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">Daftarkan Referrer</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" disabled={submitting}>
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nama</label>
              <input type="text" required value={form.name} onChange={set('name')}
                className="w-full border border-gray-300 rounded-md px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
              <input type="text" required value={form.whatsapp} onChange={set('whatsapp')}
                placeholder="08xxxxxxxxxx"
                className="w-full border border-gray-300 rounded-md px-3 py-2" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email akun Kloa</label>
            <input type="email" required value={form.email} onChange={set('email')}
              className="w-full border border-gray-300 rounded-md px-3 py-2" />
            <p className="mt-1 text-xs text-gray-500">
              Kalau email ini sudah punya akun Kloa, programnya langsung diaktifkan di akun itu.
              Kalau belum, akun baru dibuat dan tautan set-password dikirim ke sini.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nama usaha</label>
              <input type="text" required value={form.businessName} onChange={set('businessName')}
                className="w-full border border-gray-300 rounded-md px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Jenis usaha</label>
              <select required value={form.businessType} onChange={set('businessType')}
                className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="retail">Retail</option>
                <option value="fnb">F&amp;B</option>
                <option value="fashion">Fashion</option>
                <option value="bengkel">Bengkel</option>
                <option value="salon">Salon</option>
                <option value="other">Lainnya</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kota</label>
              <input type="text" value={form.city ?? ''} onChange={set('city')}
                className="w-full border border-gray-300 rounded-md px-3 py-2" />
            </div>
          </div>

          {/* Backend mewajibkan ini saat jenis usahanya "Lainnya"; tanpa field
              sendiri, pendaftarannya ditolak 422 tanpa cara memperbaikinya. */}
          {form.businessType === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sebutkan jenis usaha</label>
              <input type="text" required value={form.otherBusinessType ?? ''} onChange={set('otherBusinessType')}
                className="w-full border border-gray-300 rounded-md px-3 py-2" />
            </div>
          )}

          <div className="border-t pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Persetujuan S&amp;K</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Versi S&amp;K</label>
                <input type="text" required value={form.termsVersion} onChange={set('termsVersion')}
                  placeholder="v1.0"
                  className="w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal setuju S&amp;K</label>
                <input type="date" required value={form.termsAcceptedAt} onChange={set('termsAcceptedAt')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal setuju privasi</label>
                <input type="date" value={form.privacyAcceptedAt ?? ''} onChange={set('privacyAcceptedAt')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sumber pendaftaran</label>
            <select value={form.registeredVia} onChange={set('registeredVia')}
              className="w-full border border-gray-300 rounded-md px-3 py-2">
              <option value="google_form">Google Form</option>
              <option value="admin">Didaftarkan admin</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">
              Batal
            </button>
            <button type="submit" disabled={submitting}
              className="px-4 py-2 rounded-md text-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
              {submitting ? 'Mendaftarkan...' : 'Daftarkan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterReferrerModal;
